import { z } from 'zod';
import { AppliedDiscountSchema } from './AppliedDiscount';
import { OrderModifierGroupSchema } from './OrderModifierGroup';
import { KitchenSendPrioritySchema, KitchenStatusSchema, OrderItemVoidInfoSchema } from './types';

export const OrderItemSchema = z.object({
    orderItemId: z.number().int().positive(),
    orderItemGuid: z.string().min(1),
    // menuItemId: 0 indicates external/unmapped item (e.g., from Uber Eats, DoorDash)
    menuItemId: z.number().int().nonnegative(),
    // External item identifier for third-party platform orders (Uber Eats, DoorDash, Grubhub)
    externalItemId: z.string().optional(),
    cartItemId: z.string().optional(),
    externalSource: z.enum(['ubereats', 'doordash', 'grubhub', 'square', 'toast', 'internal']).optional(),
    name: z.string().min(1),
    quantity: z.number().int().positive().default(1),
    price: z.number().min(0).default(0), // Unit price (base or selected size price)
    modifierGroups: z.array(OrderModifierGroupSchema).default([]),
    discounts: z.array(AppliedDiscountSchema).default([]),
    isTaxable: z.boolean().default(true),
    applicableTaxRateIds: z.array(z.number().int()).default([]),
    notes: z.string().optional(),
    seatNumber: z.number().int().positive().optional(),
    isPending: z.boolean().default(true),

    // Kitchen workflow fields
    kitchenStatus: KitchenStatusSchema.default('pending'),
    sentToKitchenAt: z.coerce.date().optional(),
    readyAt: z.coerce.date().optional(),
    servedAt: z.coerce.date().optional(),
    firedAt: z.coerce.date().optional(),
    kitchenPriority: KitchenSendPrioritySchema.default('normal'),
    kitchenTicketId: z.string().optional(),
    kitchenNotes: z.string().optional(),
    courseId: z.number().int().positive().optional(),
    menuGroupId: z.number().int().optional(),

    // Void tracking
    voidInfo: OrderItemVoidInfoSchema.optional(),

    // Check splitting & transfer fields
    splitOriginGuid: z.string().optional(),
    transferredFromOrderGuid: z.string().optional(),
    transferredAt: z.coerce.date().optional(),
    isShared: z.boolean().optional(),
    splitPartsCount: z.number().int().positive().optional(),
    // Set by the POS when the operator pressed Hold with the line unsent.
    heldAt: z.coerce.date().optional(),
    // The menu item was "Exclude from discounts" when rung: discounts skip the
    // line, comps still reach it.
    excludeFromDiscounts: z.boolean().optional(),
    // Composite ("choose N") structure — see IOrderItem.isComposite. The PARENT
    // is the priced container that never reaches a ticket; every PICK names it.
    isComposite: z.boolean().optional(),
    bundleParentGuid: z.string().min(1).optional(),

    // Tip attribution (%-of-sales pooling): sales-category snapshot + serving
    // employee, stamped at ring-up.
    salesCategoryId: z.number().int().nonnegative().optional(),
    attributedEmployeeId: z.number().int().positive().optional(),
    /** Uber Eats per-item price type (e.g. 'BASE_PRICE', 'CUSTOMIZATION'). */
    priceType: z.string().optional(),
});

export const OrderItemDataSchema = z.object({
    orderItemId: z.number().int().positive(),
    orderItemGuid: z.string().min(1),
    // menuItemId: 0 indicates external/unmapped item (e.g., from Uber Eats, DoorDash)
    menuItemId: z.number().int().nonnegative(),
    // External item identifier for third-party platform orders (Uber Eats, DoorDash, Grubhub)
    externalItemId: z.string().optional(),
    cartItemId: z.string().optional(),
    externalSource: z.enum(['ubereats', 'doordash', 'grubhub', 'square', 'toast', 'internal']).optional(),
    name: z.string().min(1),
    quantity: z.number().int().positive().optional().default(1),
    price: z.number().min(0).optional().default(0), // Unit price (base or selected size price)
    modifierGroups: z.array(OrderModifierGroupSchema).optional().default([]),
    discounts: z.array(AppliedDiscountSchema).optional().default([]),
    isTaxable: z.boolean().optional().default(true),
    applicableTaxRateIds: z.array(z.number().int()).optional().default([]),
    notes: z.string().optional(),
    seatNumber: z.number().int().positive().optional(),
    isPending: z.boolean().optional().default(true),
    kitchenStatus: KitchenStatusSchema.optional().default('pending'),
    sentToKitchenAt: z.coerce.date().optional(),
    readyAt: z.coerce.date().optional(),
    servedAt: z.coerce.date().optional(),
    firedAt: z.coerce.date().optional(),
    kitchenPriority: KitchenSendPrioritySchema.optional().default('normal'),
    kitchenTicketId: z.string().optional(),
    kitchenNotes: z.string().optional(),
    courseId: z.number().int().positive().optional(),
    menuGroupId: z.number().int().optional(),
    voidInfo: OrderItemVoidInfoSchema.optional(),

    // Check splitting & transfer fields
    splitOriginGuid: z.string().optional(),
    transferredFromOrderGuid: z.string().optional(),
    transferredAt: z.coerce.date().optional(),
    isShared: z.boolean().optional(),
    splitPartsCount: z.number().int().positive().optional(),
    // Set by the POS when the operator pressed Hold with the line unsent.
    heldAt: z.coerce.date().optional(),
    // The menu item was "Exclude from discounts" when rung: discounts skip the
    // line, comps still reach it.
    excludeFromDiscounts: z.boolean().optional(),
    // Composite ("choose N") structure — see IOrderItem.isComposite. The PARENT
    // is the priced container that never reaches a ticket; every PICK names it.
    isComposite: z.boolean().optional(),
    bundleParentGuid: z.string().min(1).optional(),

    // Tip attribution (%-of-sales pooling): sales-category snapshot + serving
    // employee, stamped at ring-up.
    salesCategoryId: z.number().int().nonnegative().optional(),
    attributedEmployeeId: z.number().int().positive().optional(),
    /** Uber Eats per-item price type (e.g. 'BASE_PRICE', 'CUSTOMIZATION'). */
    priceType: z.string().optional(),
});

export type OrderItemSchemaType = z.infer<typeof OrderItemSchema>;
export type OrderItemDataSchemaType = z.infer<typeof OrderItemDataSchema>;
